"use client";

import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { PageHeader } from "@/components/page-header";
import { supabase } from "@/lib/supabase";
import { useCallback, useEffect, useState } from "react";

type ArchivedProject = {
  id: string;
  name: string;
  project_number: string | null;
  owner_id: string;
  owner_name: string | null;
  archived_at: string;
  archived_by: string | null;
  archived_by_name: string | null;
  created_at: string;
};

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
};

const columns: DataTableColumn<ArchivedProject>[] = [
  {
    header: "Project",
    render: (row) => row.name,
  },
  {
    header: "Project #",
    render: (row) => row.project_number ?? "—",
    width: "w-[120px]",
  },
  {
    header: "Owner",
    render: (row) => row.owner_name ?? row.owner_id.slice(0, 8),
    width: "w-[180px]",
  },
  {
    header: "Archived",
    render: (row) => formatDate(row.archived_at),
    width: "w-[180px]",
  },
  {
    header: "Archived by",
    render: (row) => row.archived_by_name ?? (row.archived_by ? row.archived_by.slice(0, 8) : "—"),
    width: "w-[180px]",
  },
  {
    header: "Created",
    render: (row) => formatDate(row.created_at),
    width: "w-[150px]",
  },
];

async function fetchArchivedProjects(): Promise<ArchivedProject[]> {
  const { data: projects, error } = await supabase
    .from("projects")
    .select("id, name, project_number, owner_id, archived_at, archived_by, created_at")
    .not("archived_at", "is", null)
    .order("archived_at", { ascending: false });

  if (error || !projects) return [];

  const userIds = [
    ...new Set([
      ...projects.map((p) => p.owner_id),
      ...projects.map((p) => p.archived_by).filter(Boolean),
    ]),
  ] as string[];

  const { data: profiles } = await supabase
    .from("profiles")
    .select("user_id, display_name, username")
    .in("user_id", userIds);

  const nameMap = new Map<string, string>();
  for (const p of profiles ?? []) {
    nameMap.set(p.user_id, p.display_name?.trim() || p.username?.trim() || p.user_id.slice(0, 8));
  }

  return projects.map((p) => ({
    id: p.id,
    name: p.name,
    project_number: p.project_number ?? null,
    owner_id: p.owner_id,
    owner_name: nameMap.get(p.owner_id) ?? null,
    archived_at: p.archived_at,
    archived_by: p.archived_by ?? null,
    archived_by_name: p.archived_by ? (nameMap.get(p.archived_by) ?? null) : null,
    created_at: p.created_at,
  }));
}

export function ArchivedProjectsPanel({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = useState<ArchivedProject[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setRows(await fetchArchivedProjects());
    setLoading(false);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        variant="collection"
        title="Archived Projects"
        onBack={onBack}
      />
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
        <div className="mx-auto w-full max-w-4xl">
          <DataTable
            columns={columns}
            data={rows}
            getRowKey={(row) => row.id}
            loading={loading}
            searchFilter={(row, q) =>
              row.name.toLowerCase().includes(q.toLowerCase()) ||
              (row.project_number ?? "").toLowerCase().includes(q.toLowerCase()) ||
              (row.owner_name ?? "").toLowerCase().includes(q.toLowerCase())
            }
            onRefresh={() => void load()}
            emptyMessage="No archived projects."
          />
        </div>
      </div>
    </div>
  );
}
